'use client';

import { forwardRef } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AccordionSectionProps {
  icon: string;
  title: string;
  statusText: string;
  isOpen: boolean;
  isComplete: boolean;
  isDisabled: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

export const AccordionSection = forwardRef<HTMLDivElement, AccordionSectionProps>(
  ({ icon, title, statusText, isOpen, isComplete, isDisabled, onToggle, children }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'mb-2 rounded-xl overflow-hidden border transition-all duration-300',
          isOpen ? 'border-[#4DA3FF] shadow-lg shadow-blue-100' : 'border-gray-200',
          isDisabled && 'opacity-50'
        )}
      >
        {/* Header */}
        <button
          onClick={onToggle}
          disabled={isDisabled}
          className={cn(
            'w-full px-4 py-3.5 flex items-center justify-between transition-colors',
            isComplete ? 'bg-blue-50' : 'bg-white',
            !isDisabled && 'hover:bg-gray-50',
            isDisabled && 'cursor-not-allowed'
          )}
        >
          <div className="flex items-center gap-3">
            <span className="text-xl">{icon}</span>
            <div className="text-left">
              <div className="font-bold text-gray-900">{title}</div>
              <div
                className={cn(
                  'text-xs mt-0.5 flex items-center gap-1',
                  isComplete ? 'text-[#4DA3FF]' : 'text-gray-400'
                )}
              >
                {isComplete && <Check className="w-3 h-3" />}
                {statusText}
              </div>
            </div>
          </div>
          <ChevronDown
            className={cn(
              'w-5 h-5 text-gray-400 transition-transform duration-300',
              isOpen && 'rotate-180'
            )}
          />
        </button>

        {/* Content */}
        <div
          className={cn(
            'overflow-hidden transition-all duration-300 ease-in-out bg-white',
            isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
          )}
        >
          <div className="px-4 pb-4 pt-2">{children}</div>
        </div>
      </div>
    );
  }
);

AccordionSection.displayName = 'AccordionSection';
